import angular from "angular";

import {
  ApiCrudObject,
  ApiListItem,
  Attachment,
  Record,
  Records,
  WorkflowData,
  WorkflowEvent,
} from "@salaxy/core";

import {
  UiHelpers,
  UploadService,
} from "../../services";

import { ApiCrudObjectController } from "../bases/ApiCrudObjectController";

type WorkflowPayload = ApiCrudObject & { workflowData: WorkflowData };

function isWorkflowPayload(model: WorkflowPayload | ApiListItem): model is WorkflowPayload {
  return (model as WorkflowPayload).workflowData !== undefined;
}

function isMessagesListItem(model: WorkflowPayload | ApiListItem): model is ApiListItem {
  return (model as ApiListItem).messages !== undefined;
}

/**
 * Controller for implementing workflow badge logic.
 */
export class WorkflowBadgeController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "UiHelpers",
    "Records",
    "UploadService",
  ];

 /**
  * API CRUD controller for the object.
  */
  public apiCtrl: ApiCrudObjectController<any, any>;

  /**
   * wfTypes to show as badges.
   */
  public wfTypes: string[];

  /** Bound model: ApiListItem or object with WorkflowData */
  public model: ApiListItem | WorkflowPayload | null;

  /** Visual mode of the component: horizontal or vertical (default). */
  public mode: "horizontal" | "vertical";

  private _events: WorkflowEvent[] = [];

  /**
   * Creates a new WorkflowBadgeController
   * @ignore
   */
  constructor(
    private uiHelpers: UiHelpers,
    private records: Records,
    private uploadService: UploadService,
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    // no initializations
  }

  /** Workflow events for ui. */
  public get events() {
    if (isWorkflowPayload(this.model)) {
      const eventsOfTypes = (this.model.workflowData?.events ?? []).filter( (x) => !this.wfTypes || this.wfTypes.length == 0 || this.wfTypes.indexOf(x.type) >= 0);
      this.modifyArray(eventsOfTypes, this._events);
    }
    else if (isMessagesListItem(this.model)){
      const eventsOfTypes = [];
      for(const msg of this.model.messages)  {
        const eventOfType = this.toWorkflowEvent(msg);
        if (!this.wfTypes || this.wfTypes.length == 0 || this.wfTypes.indexOf(eventOfType.type) >=0) {
          eventsOfTypes.push(eventOfType);
        }
      }
      this.modifyArray(eventsOfTypes, this._events);
    }
    else {
      this.modifyArray([], this._events);
    }

    return this._events;
  }

  /** Opens a display for workflow event details */
  public showEventDetails(original: WorkflowEvent) {
    const ensureItem = (): Promise<any> => {
      if (isMessagesListItem(this.model)) {
        // Support old worker based lists
        const id = this.apiCtrl.getApi().getBaseUrl() === "/accounts/workers" ?
        this.model.otherId : this.model.id;
        return this.apiCtrl.getApi().getSingle(id);
      } else {
        return Promise.resolve(this.model);
      }
    };

    const getWorkflowEvent = () : Promise<{event: WorkflowEvent, record: Record }> => {
      if (original?.id) {
        return ensureItem().then( (item ) => {
          return  Promise.resolve(item.workflowData.events.find(x => x.id == original.id)).then( result => {
            if (!result) {
              // Return original
              return { event: original, record: null };
            }
            if (!result.record) {
              return { event: result, record: null};
            }
            return this.records.getSingle(result.record).then( (record) =>{
              return { event: result, record };
            });
          });
        });
        }
      return Promise.resolve({event: original, record: null});
    };

    getWorkflowEvent().then( (result) => {
      this.uiHelpers.openEditDialog(
        "salaxy-components/modals/workflow/WorkflowBadgeDetails.html",
        result,
        {
          getAttachmentDownloadUrl: (attachment) => this.getAttachmentDownloadUrl(attachment),
          getAttachmentIcon: (attachment) => this.getAttachmentIcon(attachment),
        }, "lg"
      );
    });

  }

    /** Returns file icon for the attachment. */
    private getAttachmentIcon(attachment: Attachment): string {
      return this.uploadService.getFileIcon(attachment?.fileName);
    }

    /** Returns download url for the attachment. */
    private getAttachmentDownloadUrl(attachment: Attachment): string {
      return this.uploadService.getFileUrl(attachment?.contentId);
    }
  private toWorkflowEvent(messageString: string): WorkflowEvent {
    if (!messageString) {
      return {};
    }

    // [type] (Ui[ui]):[message] ([User] at [yyyy-MM-dd HH:mm] to [assignedTo] id [id])

    const uiStartX = messageString.indexOf("(Ui");
    const uiEndX = messageString.indexOf("):", uiStartX + 3);
    const type = (messageString.substring(0, uiStartX - 1) || "");
    const ui = messageString.substring(uiStartX + 3, uiEndX).toLowerCase();
    let rest = messageString.substring(uiEndX + 3);
    const messageEndX = rest.lastIndexOf("(");
    const message = rest.substring(0, messageEndX).trim();
    rest = rest.substring(messageEndX + 1); // Remove (
    rest = rest.substring(0, rest.length - 1); // Remove )

    let id = "";
    const idx = rest.lastIndexOf(" id ");
    if (idx >= 0) {
      id = rest.substring(idx + 4);
      rest = rest.substring(0, idx);
    }

    let assignedTo = "";
    const aIdx = rest.lastIndexOf(" to ");
    if (aIdx >= 0) {
      assignedTo = rest.substring(aIdx + 4);
      rest = rest.substring(0, aIdx);
    }

    let time = "";
    const tIdx = rest.lastIndexOf(" at ");
    if (tIdx >= 0) {
      time = rest.substring(tIdx + 4);
      rest = rest.substring(0, tIdx);
      // time
      time = time.replace(" ", "T")+"Z";
    }
    const user = rest;
    return {
      id,
      type,
      ui,
      user,
      assignedTo,
      message,
      time,
    } as WorkflowEvent;
  }

  private modifyArray(
    source: any[],
    target: any[]) {
    if (angular.equals(source, target)) {
      return;
    }
    target.splice(0, target.length);
    target.push(...source);
  }
}
